import React, { useState, useRef } from 'react';
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline';

interface TableDisplayProps {
  children: React.ReactNode;
}

export default function TableDisplay({ children }: TableDisplayProps) {
  const [copied, setCopied] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    try {
      if (!tableRef.current) return;

      // Find the table element within our ref
      const table = tableRef.current.querySelector('table');
      if (!table) return;

      let textContent = '';

      // Process header
      const header = table.querySelector('thead tr');
      if (header) {
        const headerCells = Array.from(header.querySelectorAll('th')).map(cell =>
          cell.textContent?.trim() || ''
        );
        textContent += headerCells.join('\t') + '\n';
      }

      // Process body rows
      const bodyRows = table.querySelectorAll('tbody tr');
      bodyRows.forEach(row => {
        const cells = Array.from(row.querySelectorAll('td')).map(cell =>
          cell.textContent?.trim() || ''
        );
        textContent += cells.join('\t') + '\n';
      });

      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy table:', error);
    }
  };

  return (
    <div className="relative my-4">
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-surface-700/90 hover:bg-surface-600 transition-colors"
        title="Copy table data"
      >
        {copied ? (
          <CheckIcon className="w-4 h-4 text-green-400" />
        ) : (
          <ClipboardIcon className="w-4 h-4 text-surface-400" />
        )}
      </button>

      {/* Table wrapper with horizontal scroll */}
      <div className="overflow-x-auto rounded-lg border border-surface-700 bg-surface-800/50">
        <div className="table-container" ref={tableRef}>
          {children}
        </div>
      </div>
    </div>
  );
}